import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dog, PawPrint, Smartphone, ArrowLeft, CheckCircle, KeyRound, Phone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthPage = () => {
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'client',
  });
  const [loading, setLoading] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  
  // Phone verification state
  const [showPhoneVerify, setShowPhoneVerify] = useState(false);
  const [verifyMode, setVerifyMode] = useState('first_time'); // 'first_time' or 'forgot_password'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verifyStep, setVerifyStep] = useState(1); // 1: enter phone, 1.5: pet search, 2: enter code, 3: set password
  const [verifiedUserName, setVerifiedUserName] = useState('');
  
  // Pet search state
  const [showPetSearch, setShowPetSearch] = useState(false);
  const [petSearchName, setPetSearchName] = useState('');
  const [petSearchResults, setPetSearchResults] = useState([]);
  
  const { login, register, setUser, setToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/setup-status`);
      if (response.data.setup_required) {
        navigate('/setup');
      }
    } catch (error) {
      console.error('Failed to check setup status:', error);
    } finally {
      setCheckingSetup(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(loginForm.username, loginForm.password);
      toast.success(`Welcome back, ${user.full_name}!`);
      navigateByRole(user.role);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(registerForm);
      toast.success(`Welcome to BowWowMeow, ${user.full_name}!`);
      navigateByRole(user.role);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Phone verification handlers
  const handleRequestCode = async () => {
    if (!phoneNumber || phoneNumber.replace(/\D/g, '').length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/request-verification`, {
        phone: phoneNumber
      });
      setVerifiedUserName(res.data.user_name || '');
      setVerifyStep(2);
      toast.success(`Code sent to ***-***-${res.data.phone_last_four}`);
    } catch (error) {
      // If phone not found, offer pet search
      if (error.response?.status === 404) {
        setShowPetSearch(true);
      }
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  // Pet search handlers
  const handlePetSearch = async () => {
    if (petSearchName.length < 2) {
      toast.error('Please enter at least 2 characters');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/search-by-pet`, {
        pet_name: petSearchName
      });
      setPetSearchResults(res.data.matches || []);
      if (res.data.matches?.length === 0) {
        toast.error('No pets found with that name');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
      setPetSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOwner = async (owner) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/send-code-to-owner`, {
        owner_id: owner.owner_id
      });
      setPhoneNumber(res.data.phone_normalized); // Set the actual phone for verification
      setVerifiedUserName(res.data.user_name || '');
      setShowPetSearch(false);
      setPetSearchResults([]);
      setVerifyStep(2);
      toast.success(`Code sent to ***-***-${res.data.phone_last_four}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/verify-code`, {
        phone: phoneNumber,
        code: verificationCode
      });
      setVerifyStep(3);
      toast.success('Phone verified! Now create your password.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/set-password`, {
        phone: phoneNumber,
        code: verificationCode,
        password: newPassword
      });
      
      // Log in the user
      localStorage.setItem('token', res.data.access_token);
      setToken(res.data.access_token);
      setUser(res.data.user);
      
      toast.success('Account set up successfully!');
      
      // Check if needs onboarding
      if (res.data.user.needs_onboarding) {
        navigate('/client/onboarding');
      } else {
        navigateByRole(res.data.user.role);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  const resetVerification = () => {
    setShowPhoneVerify(false);
    setVerifyStep(1);
    setPhoneNumber('');
    setVerificationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setVerifiedUserName('');
    setShowPetSearch(false);
    setPetSearchName('');
    setPetSearchResults([]);
  };

  const navigateByRole = (role) => {
    switch (role) {
      case 'admin':
        navigate('/admin');
        break;
      case 'walker':
        navigate('/walker');
        break;
      default:
        navigate('/dashboard');
    }
  };

  if (checkingSetup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-orange-50 to-sky-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src="/logo.png" 
            alt="BowWowMeow" 
            className="w-24 h-24 mx-auto mb-4 rounded-full object-cover shadow-lg"
          />
          <h1 className="text-4xl font-heading font-bold text-foreground">BowWowMeow</h1>
          <p className="text-muted-foreground mt-2">Premium Pet Care Services</p>
        </div>

        {/* Phone Verification Flow */}
        {showPhoneVerify ? (
          <Card className="rounded-3xl shadow-xl border-sky-200/50">
            <CardHeader>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={resetVerification}
                className="w-fit -ml-2 mb-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to Login
              </Button>
              <CardTitle className="text-xl">
                {verifyMode === 'first_time' ? '🐾 Set Up Your Account' : '🔑 Reset Password'}
              </CardTitle>
              <CardDescription>
                {verifyStep === 1 && 'Enter your phone number to verify your identity'}
                {verifyStep === 2 && `Enter the 6-digit code sent to your phone`}
                {verifyStep === 3 && 'Create a password for your account'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Step indicator */}
              <div className="flex items-center justify-center gap-2 mb-4">
                {[1, 2, 3].map((step) => (
                  <div 
                    key={step}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                      verifyStep === step 
                        ? 'bg-sky-500 text-white scale-110' 
                        : verifyStep > step 
                          ? 'bg-green-500 text-white' 
                          : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {verifyStep > step ? <CheckCircle className="w-4 h-4" /> : step}
                  </div>
                ))}
              </div>

              {/* Step 1: Enter Phone */}
              {verifyStep === 1 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <div className="flex gap-2">
                      <div className="flex items-center px-3 bg-gray-100 rounded-l-lg border border-r-0 border-gray-200">
                        <span className="text-gray-500">+1</span>
                      </div>
                      <Input
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="rounded-l-none"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Enter the phone number on file with BowWowMeow
                    </p>
                  </div>
                  <Button 
                    onClick={handleRequestCode} 
                    className="w-full bg-sky-500 hover:bg-sky-600"
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Verification Code'}
                  </Button>
                  
                  {/* Pet Search Option */}
                  {showPetSearch && (
                    <div className="pt-4 border-t border-gray-200 space-y-3">
                      <p className="text-sm text-center text-muted-foreground">
                        Phone not recognized? Search by your pet&apos;s name instead:
                      </p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter pet name..."
                          value={petSearchName}
                          onChange={(e) => setPetSearchName(e.target.value)}
                          className="flex-1"
                        />
                        <Button 
                          onClick={handlePetSearch}
                          variant="outline"
                          disabled={loading || petSearchName.length < 2}
                        >
                          <PawPrint className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Pet Search Results */}
                      {petSearchResults.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs text-muted-foreground">Select your account:</p>
                          {petSearchResults.map((result, idx) => (
                            <Button
                              key={idx}
                              variant="outline"
                              className="w-full justify-start text-left h-auto py-3"
                              onClick={() => handleSelectOwner(result)}
                              disabled={loading}
                            >
                              <div>
                                <p className="font-medium">{result.masked_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  Pet: {result.pet_name} • Phone: {result.masked_phone}
                                </p>
                              </div>
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {!showPetSearch && (
                    <Button 
                      variant="ghost" 
                      onClick={() => setShowPetSearch(true)}
                      className="w-full text-sm text-muted-foreground"
                    >
                      <PawPrint className="w-4 h-4 mr-2" />
                      Can&apos;t find your number? Search by pet name
                    </Button>
                  )}
                </div>
              )}

              {/* Step 2: Enter Code */}
              {verifyStep === 2 && (
                <div className="space-y-4">
                  {verifiedUserName && (
                    <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
                      <p className="text-green-800">
                        Hi <strong>{verifiedUserName}</strong>! Check your phone for the code.
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Verification Code</Label>
                    <Input
                      type="text"
                      placeholder="123456"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="text-center text-2xl tracking-widest font-mono"
                      maxLength={6}
                    />
                  </div>
                  <Button 
                    onClick={handleVerifyCode} 
                    className="w-full bg-sky-500 hover:bg-sky-600"
                    disabled={loading || verificationCode.length !== 6}
                  >
                    {loading ? 'Verifying...' : 'Verify Code'}
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={handleRequestCode}
                    className="w-full text-sm"
                    disabled={loading}
                  >
                    Didn&apos;t receive code? Send again
                  </Button>
                </div>
              )}

              {/* Step 3: Set Password */}
              {verifyStep === 3 && (
                <div className="space-y-4">
                  <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
                    <CheckCircle className="w-5 h-5 text-green-600 inline mr-2" />
                    <span className="text-green-800">Phone verified! Create your password.</span>
                  </div>
                  <div className="space-y-2">
                    <Label>New Password</Label>
                    <Input
                      type="password"
                      placeholder="At least 6 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Confirm Password</Label>
                    <Input
                      type="password"
                      placeholder="Enter password again"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>
                  
                  <Button 
                    onClick={handleSetPassword} 
                    className="w-full bg-green-500 hover:bg-green-600"
                    disabled={loading || newPassword.length < 6 || newPassword !== confirmPassword}
                  >
                    {loading ? 'Setting up...' : 'Complete Setup'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
        /* Normal Login/Register UI */
        <Card className="rounded-3xl shadow-xl border-sky-200/50">
          <Tabs defaultValue="login">
            <CardHeader>
              <TabsList className="grid w-full grid-cols-1 bg-gradient-to-r from-sky-100 to-orange-100">
                <TabsTrigger value="login" data-testid="login-tab" className="data-[state=active]:bg-sky-500 data-[state=active]:text-white">Login</TabsTrigger>
              </TabsList>
            </CardHeader>
            <CardContent>
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-username">Username</Label>
                    <Input
                      id="login-username"
                      data-testid="login-username"
                      placeholder="Enter your username"
                      value={loginForm.username}
                      onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                      required
                      className="border-sky-200 focus:border-sky-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      data-testid="login-password"
                      type="password"
                      placeholder="Enter your password"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                      className="border-sky-200 focus:border-sky-400"
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="login-submit"
                    className="w-full rounded-full bg-sky-500 hover:bg-sky-600"
                    disabled={loading}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                  
                  {/* First Time, New Customer & Join Team Links */}
                  <div className="pt-4 border-t border-gray-200 space-y-2">
                    <p className="text-xs text-center text-muted-foreground mb-2">Existing Customer?</p>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                      onClick={() => {
                        setVerifyMode('first_time');
                        setShowPhoneVerify(true);
                      }}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      First time? Set up your account
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                      onClick={() => {
                        setVerifyMode('forgot_password');
                        setShowPhoneVerify(true);
                      }}
                    >
                      <KeyRound className="w-4 h-4 mr-2" />
                      Forgot password?
                    </Button>
                    
                    <div className="pt-3 mt-3 border-t border-dashed border-gray-200">
                      <p className="text-xs text-center text-muted-foreground mb-2">New here?</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="text-green-600 border-green-300 hover:bg-green-50"
                          onClick={() => navigate('/register?type=customer')}
                        >
                          <PawPrint className="w-4 h-4 mr-1" />
                          New Customer
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="text-purple-600 border-purple-300 hover:bg-purple-50"
                          onClick={() => navigate('/register?type=team')}
                        >
                          <Dog className="w-4 h-4 mr-1" />
                          Join the Team
                        </Button>
                      </div>
                    </div>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="reg-username">Username</Label>
                      <Input
                        id="reg-username"
                        data-testid="register-username"
                        placeholder="Choose username"
                        value={registerForm.username}
                        onChange={(e) => setRegisterForm({ ...registerForm, username: e.target.value })}
                        required
                        className="border-orange-200 focus:border-orange-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-email">Email</Label>
                      <Input
                        id="reg-email"
                        data-testid="register-email"
                        type="email"
                        placeholder="your@email.com"
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                        required
                        className="border-orange-200 focus:border-orange-400"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-fullname">Full Name</Label>
                    <Input
                      id="reg-fullname"
                      data-testid="register-fullname"
                      placeholder="Your full name"
                      value={registerForm.full_name}
                      onChange={(e) => setRegisterForm({ ...registerForm, full_name: e.target.value })}
                      required
                      className="border-orange-200 focus:border-orange-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-phone">Phone</Label>
                    <Input
                      id="reg-phone"
                      data-testid="register-phone"
                      placeholder="(555) 123-4567"
                      value={registerForm.phone}
                      onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                      className="border-orange-200 focus:border-orange-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-password">Password</Label>
                    <Input
                      id="reg-password"
                      data-testid="register-password"
                      type="password"
                      placeholder="Create a password"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      required
                      className="border-orange-200 focus:border-orange-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Account Type</Label>
                    <Select
                      value={registerForm.role}
                      onValueChange={(value) => setRegisterForm({ ...registerForm, role: value })}
                    >
                      <SelectTrigger data-testid="register-role" className="border-orange-200">
                        <SelectValue placeholder="Select account type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="client">Pet Owner (Client)</SelectItem>
                        <SelectItem value="walker">Walker & Sitter</SelectItem>
                        <SelectItem value="admin">Administrator</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="submit"
                    data-testid="register-submit"
                    className="w-full rounded-full bg-orange-500 hover:bg-orange-600"
                    disabled={loading}
                  >
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
        )}

        <p className="text-center text-sm text-muted-foreground mt-6">
          <PawPrint className="inline w-4 h-4 mr-1 text-sky-400" />
          Trusted by pet owners everywhere
        </p>

        {/* Install App Link */}
        <button 
          onClick={() => setShowInstallHelp(true)}
          className="flex items-center justify-center gap-2 mx-auto mt-4 text-sm text-sky-600 hover:text-sky-700 transition-colors"
        >
          <Smartphone className="w-4 h-4" />
          <span>Install App on Your Phone</span>
        </button>

        {/* Install Help Modal */}
        {showInstallHelp && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-orange-500 to-sky-500 p-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-6 h-6" />
                    <h2 className="font-bold text-lg">Install BowWowMeow</h2>
                  </div>
                  <button 
                    onClick={() => setShowInstallHelp(false)}
                    className="text-white hover:bg-white/20 rounded-full p-1"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-4">
                {/* iPhone/iPad Instructions */}
                <div className="space-y-3">
                  <h3 className="font-bold text-gray-800 flex items-center gap-2">
                    🍎 iPhone / iPad (Safari)
                  </h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                    <li>Open this site in <strong>Safari</strong></li>
                    <li>Tap the <strong>Share</strong> button (square with arrow)</li>
                    <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                    <li>Tap <strong>"Add"</strong> in the top right</li>
                  </ol>
                </div>

                <hr className="border-gray-200" />

                {/* Android Instructions */}
                <div className="space-y-3">
                  <h3 className="font-bold text-gray-800 flex items-center gap-2">
                    🤖 Android (Chrome)
                  </h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                    <li>Open this site in <strong>Chrome</strong></li>
                    <li>Tap the <strong>menu</strong> (three dots)</li>
                    <li>Tap <strong>"Add to Home Screen"</strong></li>
                    <li>Tap <strong>"Add"</strong></li>
                  </ol>
                </div>

                {/* App Icon Preview */}
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 mb-2">The app icon:</p>
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl overflow-hidden shadow-md">
                    <img src="/icons/icon-192x192.png" alt="BowWowMeow icon" className="w-full h-full" />
                  </div>
                </div>
              </div>

              <div className="p-4 border-t bg-gray-50">
                <Button 
                  onClick={() => setShowInstallHelp(false)}
                  className="w-full bg-gradient-to-r from-orange-500 to-sky-500 hover:from-orange-600 hover:to-sky-600 text-white rounded-full"
                >
                  Got it!
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthPage;
